import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
} from 'react';
import apiClient from '../lib/apiClient';
import { authFetch } from '../lib/authFetch';
import { triggerAuthFailure } from '../lib/authFailure';

type UserRole = 'super admin' | 'primary' | 'partner' | 'child';

interface User {
  id: string;
  email: string;
  role: UserRole;

  firstName?: string;
  lastName?: string;

  hasCompletedOnboarding?: boolean;
}

interface AuthState {
  token: string | null;
  user: User | null;
  loading: boolean;
}

interface AuthContextType {
  auth: AuthState;
  login: (email: string, password: string) => Promise<User>;
  logout: () => void;
  refreshUser: () => Promise<void>; // ✅ now exposed
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);
let refreshUserInFlight: Promise<void> | null = null;

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [auth, setAuth] = useState<AuthState>({
    token: null,
    user: null,
    loading: true,
  });

  const login = async (email: string, password: string): Promise<User> => {
    try {
      const res = await authFetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });

      if (!res.ok) throw new Error('Invalid credentials');

      const data = await res.json();
      localStorage.setItem('token', data.token);
      localStorage.setItem('user', JSON.stringify(data.user));

      setAuth({ token: data.token, user: data.user, loading: false });
      localStorage.setItem('token', data.token);
      localStorage.setItem('user', JSON.stringify(data.user));
      return data.user as User;
    } catch (err) {
      console.error('Login error:', err);
      throw err;
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');

    // ✅ Clear transaction filter state
    localStorage.removeItem('transactionsFilters');

    setAuth({ token: null, user: null, loading: false });
  };

  const refreshUser = async () => {
    if (refreshUserInFlight) return refreshUserInFlight;

    refreshUserInFlight = (async () => {
      try {
        const res = await apiClient.get('/api/auth/me');

        const updatedUser = res.data;

        // ✅ Save to both React state and localStorage
        setAuth(prev => ({ ...prev, user: updatedUser }));
        localStorage.setItem('user', JSON.stringify(updatedUser));
      } catch (err) {
        const maybeStatus = (err as { response?: { status?: number } })?.response?.status;
        const maybeMessage = (err as { message?: string })?.message?.toLowerCase?.() ?? '';
        if (maybeStatus === 401 || maybeMessage.includes('invalid token')) {
          triggerAuthFailure('session-expired');
        }
        console.error('Failed to refresh user:', err);
      } finally {
        refreshUserInFlight = null;
      }
    })();

    return refreshUserInFlight;
  };

  // ✅ Restore auth from localStorage on mount
  useEffect(() => {
    const storedToken = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');

    if (storedToken && storedUser) {
      setAuth({
        token: storedToken,
        user: JSON.parse(storedUser),
        loading: false,
      });

      // ✅ Optional: immediately refresh from DB
      refreshUser();
    } else {
      setAuth({ token: null, user: null, loading: false });
    }
  }, []);

  return (
    <AuthContext.Provider value={{ auth, login, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within an AuthProvider');
  return context;
};



export { AuthContext }; // ✅ Add this export for useContext in Dashboard
